import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface ReturnToVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    reason: string;
    defect_description: string;
    severity: 'minor' | 'major' | 'critical';
    corrective_action: string;
    remarks: string;
  }) => void;
}

const RETURN_REASONS = [
  'Defective Work',
  'Incomplete Work',
  'Wrong Parts Used',
  'Safety Issue',
  'Not Performed',
];

export const ReturnToVendorModal: React.FC<ReturnToVendorModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [reason, setReason] = useState(RETURN_REASONS[0]);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<'minor' | 'major' | 'critical'>('major');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [remarks, setRemarks] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!description.trim()) newErrors.description = 'Description is required';
    if (!correctiveAction.trim()) newErrors.correctiveAction = 'Corrective action is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      reason,
      defect_description: description,
      severity,
      corrective_action: correctiveAction,
      remarks,
    });
    setDescription('');
    setCorrectiveAction('');
    setRemarks('');
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onClose}>Cancel</Button>
      <Button variant="danger" onClick={handleSubmit}>Return to Vendor</Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Return Work to Vendor" footer={footer}>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-300">Reason for Return</label>
          <select
            value={reason}
            onChange={e => setReason(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-xs text-slate-200 focus:border-sky-500 focus:outline-none"
          >
            {RETURN_REASONS.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-300">Defect Description *</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            placeholder="Describe the defect or issue found..."
            className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs text-slate-200 placeholder-slate-500 focus:border-sky-500 focus:outline-none resize-none"
          />
          {errors.description && <p className="text-xs text-rose-400">{errors.description}</p>}
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-300">Defect Severity</label>
          <div className="flex gap-3">
            {(['minor', 'major', 'critical'] as const).map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setSeverity(s)}
                className={`flex-1 p-2.5 rounded-lg border text-xs font-semibold capitalize transition-colors ${
                  severity === s
                    ? s === 'critical'
                      ? 'bg-rose-500/10 border-rose-500/50 text-rose-400'
                      : s === 'major'
                      ? 'bg-amber-500/10 border-amber-500/50 text-amber-400'
                      : 'bg-sky-500/10 border-sky-500/50 text-sky-400'
                    : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-300">Required Corrective Action *</label>
          <textarea
            value={correctiveAction}
            onChange={e => setCorrectiveAction(e.target.value)}
            rows={2}
            placeholder="What must the vendor do to fix this?"
            className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs text-slate-200 placeholder-slate-500 focus:border-sky-500 focus:outline-none resize-none"
          />
          {errors.correctiveAction && <p className="text-xs text-rose-400">{errors.correctiveAction}</p>}
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-300">Additional Remarks</label>
          <textarea
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            rows={2}
            placeholder="Any additional notes..."
            className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs text-slate-200 placeholder-slate-500 focus:border-sky-500 focus:outline-none resize-none"
          />
        </div>
      </div>
    </Modal>
  );
};
